/**
 * AI API service for content generation and model management.
 * @module lib/api/ai
 */

import type { AIModel } from '@scrambl/shared'

import { apiClient } from './api-client'
import { buildQueryString } from './base-api'
import { createSingletonApi } from './singleton-factory'

export interface AIGenerateRequest {
  action: string
  prompt: string
  context?: Record<string, unknown>
  systemPromptKey?: string
}

export interface AIGeneratedTask {
  name?: string
  description?: string
  priority?: string
  estimatedHours?: number
  taskTypeDefId?: string
}

export interface AIGeneratedTaskGroup {
  existingGroupName?: string
  name?: string
  description?: string
  color?: string
  tasks?: AIGeneratedTask[]
}

export interface AIGeneratedProjectTasks {
  groups: AIGeneratedTaskGroup[]
  ungroupedTasks: AIGeneratedTask[]
}

export interface AIGeneratedSubtasks {
  subtasks: AIGeneratedTask[]
}

export interface AIGenerateResponse {
  content: string
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  /**
   * Backend-parsed, normalized structure for task/subtask generation
   * (`generate-project-tasks`, `generate-subtasks`). Present only for those
   * actions; consumers should prefer this over parsing `content` themselves.
   */
  parsed?: AIGeneratedProjectTasks | AIGeneratedSubtasks
}

export interface AIModelWithProvider extends AIModel {
  provider: {
    id: string
    name: string
    type: string
  }
}

export interface AIUsage {
  id: string
  feature: string
  promptTokens: number
  responseTokens: number
  totalTokens: number
  creditsUsed: number
  status: string
  createdAt: string
  user: {
    id: string
    email: string
    firstName?: string
    lastName?: string
  }
  model: AIModelWithProvider
}

export interface AIUsageFilters {
  startDate?: string
  endDate?: string
  feature?: string
  userId?: string
}

export interface AIUsageTotals {
  count: number
  promptTokens: number
  responseTokens: number
  totalTokens: number
  creditsUsed: number
  successfulCount: number
}

export interface AIUsageResponse {
  usage: AIUsage[]
  totals: AIUsageTotals
}

export interface AIUsagePaginatedParams extends AIUsageFilters {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface AIUsagePaginatedResponse {
  data: AIUsage[]
  meta: {
    currentPage: number
    itemsPerPage: number
    totalItems: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
  totals: AIUsageTotals
}

export interface AICreditStatus {
  configured: boolean
  remainingCredits: number
  monthlyCredits: number | null
  creditsUsedThisPeriod: number | null
  periodEnd: string | null
  requiredCredits: number
  canUse: boolean
  feature?: string
}

class AIApiService {
  async getCreditStatus(feature?: string): Promise<AICreditStatus> {
    const query = feature ? `?feature=${encodeURIComponent(feature)}` : ''
    const response = await apiClient<{ data: AICreditStatus }>(
      `/ai/credits${query}`,
    )
    return response.data
  }

  /**
   * Generates content using AI
   * @param request - Generation request parameters
   * @returns Generated content and usage stats
   */
  async generate(request: AIGenerateRequest): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/generate',
      {
        method: 'POST',
        body: request,
      },
    )
    return response.data
  }

  /**
   * Generates a description for an entity
   * @param entityType - Type of entity
   * @param context - Context for generation
   * @returns Generated description
   */
  async generateDescription(
    entityType: string,
    context: Record<string, unknown>,
  ): Promise<string> {
    const response = await this.generate({
      action: 'generate-description',
      prompt: `Generate a clear and concise description for a ${entityType}. Context: ${JSON.stringify(context)}`,
      context,
    })
    return response.content
  }

  /**
   * Generates tasks for a project
   * @param projectName - Name of the project
   * @param projectDescription - Description of the project
   * @param additionalContext - Optional additional context
   * @returns Array of generated task descriptions
   */
  async generateProjectTasks(
    projectName: string,
    projectDescription: string,
    additionalContext?: string,
  ): Promise<string[]> {
    const prompt = `Generate a list of tasks for the following project:

Project Name: ${projectName}
Project Description: ${projectDescription}
${additionalContext ? `Additional Context: ${additionalContext}` : ''}

Please provide a list of actionable tasks that would help complete this project. Each task should be clear, specific, and achievable.`

    const response = await this.generate({
      action: 'generate-project-tasks',
      prompt,
      context: { projectName, projectDescription, additionalContext },
    })

    return response.content
      .split('\n')
      .map(task => task.trim())
      .filter(task => task.length > 0 && !task.startsWith('#'))
  }

  /**
   * Gets available AI models
   * @returns Array of available models with provider info
   */
  async getModels(): Promise<AIModelWithProvider[]> {
    const response = await apiClient<{ data: AIModelWithProvider[] }>(
      '/ai/models',
    )
    return response.data
  }

  /**
   * Gets organization's model preference
   * @returns Current model preference or null
   */
  async getModelPreference(): Promise<{ model: AIModelWithProvider } | null> {
    const response = await apiClient<{
      data: { model: AIModelWithProvider } | null
    }>('/ai/models/preference')
    return response.data
  }

  /**
   * Gets AI usage statistics
   * @param filters - Optional filters
   * @returns Usage data and totals
   */
  async getUsage(filters?: AIUsageFilters): Promise<AIUsageResponse> {
    const queryString = filters ? buildQueryString(filters) : ''
    const response = await apiClient<{ data: AIUsageResponse }>(
      `/ai/usage${queryString}`,
    )
    return response.data
  }

  /**
   * Gets paginated AI usage statistics
   * @param params - Pagination and filter parameters
   * @returns Paginated usage data with totals
   */
  async getUsagePaginated(
    params: AIUsagePaginatedParams = {},
  ): Promise<AIUsagePaginatedResponse> {
    const queryString = buildQueryString(params)
    return apiClient<AIUsagePaginatedResponse>(`/ai/usage${queryString}`)
  }
}

export const aiApi = createSingletonApi(AIApiService)
